import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ClipboardList, Home as HomeIcon, Users, User, ArrowRight, Check, ArrowLeft } from "lucide-react";
import { useCareRecipientStore } from "@/store/careRecipientStore";

const HERO = "https://static.prod-images.emergentagent.com/jobs/67e85589-df3b-4949-9f26-d13809d545ec/images/369110568c1e38ef77cac9d3563a0c8d1bdeed84b8c97f464ef74592a5f8a3b9.jpeg";
const INSET = "https://static.prod-images.emergentagent.com/jobs/67e85589-df3b-4949-9f26-d13809d545ec/images/e78f176d12e9283b8c453aeb330100046fb3138c52b425eb7af7636efdf2e86d.jpeg";

const FEATURES = [
  { Icon: ShieldCheck, label: "Trusted medical providers" },
  { Icon: ClipboardList, label: "Personalised care plans" },
  { Icon: HomeIcon, label: "At home & on-demand services" },
  { Icon: Users, label: "Family stays informed" },
];

export default function SeniorCare() {
  const navigate = useNavigate();
  const { mode, setMode } = useCareRecipientStore();
  const [selected, setSelected] = useState(mode || null);

  const start = () => {
    const chosen = selected || "self";
    setMode(chosen);
    navigate(chosen === "guardian" ? "/senior-care/guardian" : "/senior-care/dashboard");
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} data-testid="senior-care-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="text-center">
        <h1 className="text-4xl font-extrabold" style={{ color: "#1E3A8A" }}>Senior Care</h1>
        <p className="mt-2 text-muted-foreground">Complete healthcare &amp; medical support for you or your parents.</p>
      </div>

      {/* Hero */}
      <div className="relative rounded-3xl overflow-hidden">
        <img src={HERO} alt="Senior care" className="w-full h-64 object-cover" />
        <div className="absolute right-3 top-3 w-36 rounded-2xl bg-white p-1.5 shadow-lg">
          <img src={INSET} alt="Connected" className="w-full h-24 rounded-xl object-cover" />
          <p className="text-[11px] font-semibold text-center py-1.5" style={{ color: "#1E3A8A" }}>Always connected across distances</p>
        </div>
      </div>

      {/* Features */}
      <div className="space-y-4">
        {FEATURES.map((f) => (
          <div key={f.label} className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "#EFF6FF" }}>
              <f.Icon className="h-5 w-5" style={{ color: "#2563EB" }} />
            </div>
            <span className="font-medium text-foreground">{f.label}</span>
          </div>
        ))}
      </div>

      {/* Personas */}
      <div className="grid grid-cols-2 gap-4">
        <button
          data-testid="persona-self"
          onClick={() => setSelected("self")}
          className="relative rounded-2xl p-6 text-center border-2 transition-all"
          style={{ backgroundColor: "#EFF6FF", borderColor: selected === "self" ? "#2563EB" : "transparent" }}
        >
          <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: "#DBEAFE" }}>
            <User className="h-7 w-7" style={{ color: "#2563EB" }} />
          </div>
          <p className="font-extrabold text-foreground">I need care for myself</p>
          {selected === "self" && <span className="absolute top-2.5 right-2.5 h-5 w-5 rounded-full flex items-center justify-center" style={{ backgroundColor: "#2563EB" }}><Check className="h-3 w-3 text-white" /></span>}
        </button>
        <button
          data-testid="persona-guardian"
          onClick={() => setSelected("guardian")}
          className="relative rounded-2xl p-6 text-center border-2 transition-all"
          style={{ backgroundColor: "#F3F0FF", borderColor: selected === "guardian" ? "#7C3AED" : "transparent" }}
        >
          <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: "#EDE9FE" }}>
            <Users className="h-7 w-7" style={{ color: "#7C3AED" }} />
          </div>
          <p className="font-extrabold text-foreground">I&apos;m caring for my parents</p>
          {selected === "guardian" && <span className="absolute top-2.5 right-2.5 h-5 w-5 rounded-full flex items-center justify-center" style={{ backgroundColor: "#7C3AED" }}><Check className="h-3 w-3 text-white" /></span>}
        </button>
      </div>

      <button
        data-testid="start-care-journey"
        onClick={start}
        className="w-full flex items-center justify-center gap-2 rounded-2xl py-4 text-white text-lg font-extrabold transition-all hover:shadow-lg"
        style={{ backgroundColor: selected ? "#2563EB" : "#93C5FD" }}
      >
        Start Care Journey <ArrowRight className="h-5 w-5" />
      </button>

      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <ShieldCheck className="h-4 w-4" style={{ color: "#16A34A" }} />
        Care delivered by verified Trata Health providers &amp; hospitals
      </div>
    </div>
  );
}
